import { type ComponentProps, forwardRef, useMemo, useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import { isEqual } from "lodash";
import { useToasts } from "react-toast-notifications";

import { SecondaryButton } from "@components/v2";
import { useOverviewContext } from "@features/overview-v2/hooks";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";

import { CreateSavedViewModal, UpdateSavedViewModal } from "../modals";

const cleanObject = <T extends Record<string, any>>(obj: T): Partial<T> => {
  return Object.keys(obj).reduce((result, key) => {
    const value = obj[key];
    if (value !== null && value !== undefined) {
      result[key as keyof T] = value;
    }
    return result;
  }, {} as Partial<T>);
};

export const SaveMenu = () => {
  const { addToast } = useToasts();
  const { selectedSavedView, unsavedFilters, createSavedView, tiles } =
    useOverviewContext();

  const [isUpdatingSavedView, setIsUpdatingSavedView] = useState(false);
  const [isCreatingSavedView, setIsCreatingSavedView] = useState(false);

  const isSavedDisabled = useMemo(() => {
    const cleanedSelectedSavedView =
      selectedSavedView?.config.pageConfiguration.tiles?.map(cleanObject);
    const cleanedTiles = tiles?.map(cleanObject);
    const tilesIsEqual = isEqual(cleanedSelectedSavedView, cleanedTiles);
    const noUnsavedFilters =
      unsavedFilters === null || unsavedFilters.size === 0;

    return tilesIsEqual && noUnsavedFilters;
  }, [selectedSavedView, tiles, unsavedFilters]);

  return (
    <div>
      <Menu>
        {({ open }) => (
          <>
            <MenuButton as={SecondaryButton}>
              SAVE
              <motion.i
                initial={false}
                animate={{ rotate: open ? 0 : 180 }}
                transition={{ duration: 0.2 }}
                className="fa-regular fa-sharp fa-chevron-up ml-2"
              />
            </MenuButton>
            <MenuItems
              transition
              className={clsx(
                "z-[9999] bg-v2-primary border border-v2-tertiary rounded-md text-v2-primary",
                "w-40 elevation-1 origin-top transition duration-200 ease-out p-[1px]",
                "data-[closed]:translate-y-1 data-[closed]:opacity-0",
              )}
              anchor={{ to: "bottom end", gap: 4 }}
            >
              {!selectedSavedView?.is_default && (
                <MenuItem
                  as={Option}
                  disabled={isSavedDisabled}
                  onClick={() => setIsUpdatingSavedView(true)}
                >
                  Update existing
                </MenuItem>
              )}
              <MenuItem
                as={Option}
                onClick={() => setIsCreatingSavedView(true)}
              >
                Save as new
              </MenuItem>
            </MenuItems>
          </>
        )}
      </Menu>
      <UpdateSavedViewModal
        isOpen={isUpdatingSavedView}
        onClose={() => {
          setIsUpdatingSavedView(false);
        }}
      />
      <CreateSavedViewModal
        isOpen={isCreatingSavedView}
        onClose={() => setIsCreatingSavedView(false)}
        onSubmit={(name: string) =>
          createSavedView(
            name,
            { container: "saved-views-modal", eventType: "submit" },
            {
              errorMessage: "There was an error creating the view.",
              onSuccess: () => {
                setIsCreatingSavedView(false);
                addToast("Saved view created", {
                  appearance: "success",
                });
              },
            },
          )
        }
      />
    </div>
  );
};

const Option = forwardRef<HTMLButtonElement, ComponentProps<"button">>(
  (props, ref) => {
    return (
      <button
        ref={ref}
        className={clsx(
          "block h-9 w-full px-4 flex items-center gap-2 text-sm cursor-pointer rounded",
          "data-[disabled]:text-v2-gray/200 data-[disabled]:bg-white data-[disabled]:cursor-auto",
          "data-[focus]:bg-v2-action-tertiary data-[selected]:bg-v2-action-secondary",
          "data-[selected]:font-semibold",
        )}
        {...props}
      />
    );
  },
);
